import { combineReducers, configureStore } from "@reduxjs/toolkit";
import userReducer, { clearCredentials } from "./user-reducer";
import { thunk } from "redux-thunk";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { persistReducer, persistStore } from "redux-persist";
import axios from "axios";
import { refreshTokens } from "../api/thunks/users";
import type { RefreshTokenError } from "../api/thunks/users";
import { REFRESH_TOKEN_EXPIRED_CODE, LOGIN_ROUTE } from "../constants/auth";
import notificationsReducer from "./notifications-reducer";
import layoutReducer from "./layout-reducer";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["user", "layout"], // only user and layout will be persisted
};

const rootReducer = combineReducers({
  user: userReducer,
  notifications: notificationsReducer,
  layout: layoutReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(thunk),
});

const handleRefreshTokenExpiration = () => {
  store.dispatch(clearCredentials());
  localStorage.removeItem("credentials");
  localStorage.removeItem("persist:root");
  window.location.href = LOGIN_ROUTE;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const requestConfig = error?.config;

    if (status !== 401 || !requestConfig) {
      return Promise.reject(error);
    }

    const isRefreshRequest =
      typeof requestConfig.url === "string" &&
      requestConfig.url.includes("/v1/users/refresh");
    if (isRefreshRequest) {
      handleRefreshTokenExpiration();
      return Promise.reject(error);
    }

    const headers = (requestConfig.headers ?? {}) as Record<string, any>;
    const retryCounter =
      Number.parseInt(headers["X-PQ-Retry-Counter"], 10) || 0;
    if (retryCounter >= 3) {
      return Promise.reject(error);
    }
    headers["X-PQ-Retry-Counter"] = retryCounter + 1;
    requestConfig.headers = headers;

    const resultAction = await store.dispatch(refreshTokens());

    if (refreshTokens.fulfilled.match(resultAction)) {
      const tokens = resultAction.payload;
      localStorage.setItem("credentials", JSON.stringify(tokens));
      if (tokens.accessToken) {
        requestConfig.headers["Authorization"] = tokens.accessToken;
      }

      return axios.request(requestConfig);
    }

    if (refreshTokens.rejected.match(resultAction)) {
      const payload = resultAction.payload as RefreshTokenError | undefined;
      if (payload?.code === REFRESH_TOKEN_EXPIRED_CODE) {
        handleRefreshTokenExpiration();
      }
    }

    return Promise.reject(error);
  },
);

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
